import { GenAiEmployee, InfoCategoryId } from "./model";
import { gen_ai_mock_enabled } from "./mock-mode";

/**
 * Reader for the `final-copilot-mock-data-v1` package.
 *
 * The package is 79MB of GraphQL payloads. `scripts/build-gen-ai-mock.js`
 * projects it into `public/mock/gen-ai/`, which vue-cli copies verbatim rather
 * than bundling, so nothing here reaches the app bundle and only the file
 * behind a given drill-down is ever fetched.
 *
 * Shared by the events mock and the employees modal so both read one source —
 * the card counts and the rows behind them cannot drift apart.
 */
const CACHE: Record<string, Promise<any>> = {};

const load = <T>(file: string, fallback: T): Promise<T> => {
  // Live mode must not serve the packaged files: a populated drill-down under
  // a zeroed card is worse than an empty one, because it reads as real.
  if (!gen_ai_mock_enabled()) return Promise.resolve(fallback);

  if (!CACHE[file]) {
    CACHE[file] = fetch(`/mock/gen-ai/${file}.json`)
      .then((r) => (r.ok ? r.json() : fallback))
      .catch(() => fallback);
  }
  return CACHE[file] as Promise<T>;
};

/** one widget's events, already in the modal's post-parser row shape */
export const load_rows = (file: string): Promise<any[]> => load(file, []);

export interface PackageEmployee extends GenAiEmployee {
  files: number;
  first_event: string;
  last_event: string;
}

/**
 * The people behind a "# Shared By Employees" row. Passing no category returns
 * the package's all-categories set rather than concatenating the six, which
 * would double-count anyone who appears in more than one.
 */
export const load_employees = async (
  category?: InfoCategoryId | null
): Promise<PackageEmployee[]> => {
  const all = await load<Record<string, PackageEmployee[]>>("employees", {});
  return (category ? all[category] : all.all) || [];
};

/** the package's stable label ids, keyed as the taxonomy file ships them */
export const load_taxonomy = (): Promise<any> => load("taxonomy", {});
